/**
 * AdjustPostbackTemplatePage — mobile attribution vendor (MMP) postback
 * template list with a live macro preview panel.
 *
 * UI copy is intentionally vendor-neutral: we never surface the underlying
 * vendor's brand name in labels, placeholders, tooltips, or alerts. Users
 * pick a template key, paste a URL containing {macro_name} placeholders,
 * and can preview macro substitution against a sample context.
 */
import { useMemo, useState, type FormEvent } from "react";
import {
	useMmpPostbackTemplateList,
	useMmpPostbackTemplateCreate,
	useMmpPostbackTemplateDelete,
} from "./hooks";
import {
	KNOWN_MACROS,
	extractMacros,
	renderTemplate,
	type MacroContext,
	type MmpPostbackTemplateRow,
} from "./api";

const CARD =
	"rounded-[8px] border border-zinc-200 bg-white p-5 shadow-sm";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-600/20";
const LABEL = "text-xs font-medium text-zinc-700";
const BTN =
	"rounded-[6px] border border-green-700 bg-green-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-green-800 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_GHOST =
	"rounded-[6px] border border-zinc-300 bg-white px-2.5 py-1 text-xs font-medium text-zinc-700 hover:bg-zinc-50";
const CHIP =
	"inline-flex items-center rounded-[6px] border border-green-200 bg-green-50 px-2 py-0.5 font-mono text-xs text-green-800";

const SAMPLE_CTX: MacroContext = {
	click_id: "clk_7f92a1",
	gps_adid: "6a2b1c5d-4e3f-8a9b-0c1d-2e3f4a5b6c7d",
	idfa: "AEBE52E7-03EE-455A-B3C4-E57283966239",
	event_token: "abc123",
	campaign_id: "cmp_801",
	publisher_id: "pub_42",
	install_ts: 1712000000,
	event_ts: 1712003600,
};

export function AdjustPostbackTemplatePage() {
	const listQuery = useMmpPostbackTemplateList();
	const createMut = useMmpPostbackTemplateCreate();
	const deleteMut = useMmpPostbackTemplateDelete();

	const [templateKey, setTemplateKey] = useState("");
	const [name, setName] = useState("");
	const [urlTemplate, setUrlTemplate] = useState(
		"https://partner.example.com/postback?click_id={click_id}&gps_adid={gps_adid}&event_token={event_token}",
	);
	const [eventToken, setEventToken] = useState("");

	const macros = useMemo(() => extractMacros(urlTemplate), [urlTemplate]);
	const preview = useMemo(
		() => renderTemplate(urlTemplate, SAMPLE_CTX),
		[urlTemplate],
	);
	const unknownMacros = useMemo(
		() => macros.filter((m) => !KNOWN_MACROS.includes(m)),
		[macros],
	);

	const handleCreate = (e: FormEvent) => {
		e.preventDefault();
		if (!templateKey.trim() || !name.trim() || !urlTemplate.trim()) return;
		createMut.mutate(
			{
				template_key: templateKey.trim(),
				name: name.trim(),
				url_template: urlTemplate.trim(),
				event_token: eventToken.trim() || null,
				active: true,
			},
			{
				onSuccess: () => {
					setTemplateKey("");
					setName("");
					setEventToken("");
				},
			},
		);
	};

	const items: MmpPostbackTemplateRow[] = listQuery.data?.items ?? [];

	return (
		<main
			data-testid="adjust-postback-template-page"
			className="mx-auto flex w-full max-w-5xl flex-col gap-8 px-6 py-10"
		>
			<header className="flex flex-col gap-2">
				<p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
					Outbound postback templates
				</p>
				<h1 className="bg-gradient-to-r from-green-700 to-green-500 bg-clip-text text-3xl font-semibold text-transparent">
					MMP postback template
				</h1>
				<p className="text-sm text-zinc-600">
					Named URL templates for outbound mobile attribution vendor
					postbacks. Use <span className={CHIP}>{"{macro_name}"}</span>{" "}
					placeholders inside the URL; they are substituted at send time
					with per-event values.
				</p>
			</header>

			<section className={CARD} aria-label="Create new template">
				<h2 className="text-base font-semibold text-zinc-900">
					Create new template
				</h2>
				<form
					onSubmit={handleCreate}
					className="mt-3 flex flex-col gap-3"
					data-testid="adjust-postback-template-create-form"
				>
					<div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
						<label className="flex flex-col gap-1">
							<span className={LABEL}>Template key</span>
							<input
								type="text"
								className={INPUT}
								placeholder="install_v1"
								value={templateKey}
								onChange={(e) => setTemplateKey(e.currentTarget.value)}
								disabled={createMut.isPending}
								data-testid="adjust-postback-template-key-input"
								maxLength={120}
							/>
						</label>
						<label className="flex flex-col gap-1">
							<span className={LABEL}>Display name</span>
							<input
								type="text"
								className={INPUT}
								placeholder="Install postback v1"
								value={name}
								onChange={(e) => setName(e.currentTarget.value)}
								disabled={createMut.isPending}
								data-testid="adjust-postback-template-name-input"
								maxLength={200}
							/>
						</label>
					</div>
					<label className="flex flex-col gap-1">
						<span className={LABEL}>URL template</span>
						<textarea
							className={`${INPUT} font-mono text-xs`}
							rows={3}
							placeholder="https://partner.example.com/postback?click_id={click_id}"
							value={urlTemplate}
							onChange={(e) => setUrlTemplate(e.currentTarget.value)}
							disabled={createMut.isPending}
							data-testid="adjust-postback-template-url-input"
							maxLength={2048}
						/>
					</label>
					<label className="flex flex-col gap-1">
						<span className={LABEL}>Event token (optional)</span>
						<input
							type="text"
							className={INPUT}
							placeholder="abc123"
							value={eventToken}
							onChange={(e) => setEventToken(e.currentTarget.value)}
							disabled={createMut.isPending}
							data-testid="adjust-postback-template-event-token-input"
							maxLength={64}
						/>
					</label>

					<div className="flex items-center justify-end">
						<button
							type="submit"
							className={BTN}
							disabled={
								createMut.isPending ||
								!templateKey.trim() ||
								!name.trim() ||
								!urlTemplate.trim()
							}
							data-testid="adjust-postback-template-submit"
						>
							{createMut.isPending ? "Saving" : "Save template"}
						</button>
					</div>
				</form>
				{createMut.error ? (
					<p className="mt-2 text-xs text-red-600" role="alert">
						{createMut.error.message}
					</p>
				) : null}
			</section>

			<section
				className={CARD}
				aria-label="Macro preview"
				data-testid="adjust-postback-template-preview-panel"
			>
				<h2 className="text-base font-semibold text-zinc-900">
					Macro preview
				</h2>
				<p className="mt-1 text-xs text-zinc-600">
					Live substitution against a sample event context. Unknown
					placeholders are left as-is so typos surface immediately.
				</p>

				<div className="mt-3 flex flex-wrap gap-1.5">
					{macros.length === 0 ? (
						<span className="text-xs text-zinc-500">
							No macros detected.
						</span>
					) : (
						macros.map((m) => (
							<span
								key={m}
								className={CHIP}
								data-testid={`adjust-postback-template-macro-${m}`}
							>
								{`{${m}}`}
							</span>
						))
					)}
				</div>

				{unknownMacros.length > 0 ? (
					<p
						className="mt-2 text-xs text-amber-700"
						data-testid="adjust-postback-template-unknown-macros"
					>
						Unrecognized macros: {unknownMacros.join(", ")}
					</p>
				) : null}

				<div className="mt-3">
					<span className={LABEL}>Rendered URL</span>
					<pre
						className="mt-1 overflow-x-auto rounded-[6px] border border-zinc-200 bg-zinc-50 px-3 py-2 font-mono text-xs text-zinc-900"
						data-testid="adjust-postback-template-preview-output"
					>
						{preview}
					</pre>
				</div>
			</section>

			<section className={CARD} aria-label="Saved templates">
				<h2 className="text-base font-semibold text-zinc-900">
					Saved templates
				</h2>
				{listQuery.isLoading ? (
					<p
						className="mt-3 text-sm text-zinc-500"
						data-testid="adjust-postback-template-loading"
					>
						Loading
					</p>
				) : listQuery.error ? (
					<p className="mt-3 text-sm text-red-600" role="alert">
						{listQuery.error.message}
					</p>
				) : items.length === 0 ? (
					<p
						className="mt-3 text-sm text-zinc-500"
						data-testid="adjust-postback-template-empty"
					>
						No templates yet. Create one above.
					</p>
				) : (
					<ul
						className="mt-3 divide-y divide-zinc-200"
						data-testid="adjust-postback-template-list"
					>
						{items.map((row) => (
							<li
								key={row.id}
								className="flex flex-col gap-1 py-3 text-sm sm:flex-row sm:items-start sm:justify-between"
								data-testid={`adjust-postback-template-row-${row.id}`}
							>
								<div className="flex flex-col gap-0.5">
									<span className="font-medium text-zinc-900">
										{row.name}
									</span>
									<span className="font-mono text-xs text-zinc-500">
										{row.template_key}
									</span>
									<span className="truncate font-mono text-xs text-zinc-600">
										{row.url_template}
									</span>
									<span className="text-xs text-zinc-500">
										{row.active === 1 ? "Active" : "Inactive"}
										{row.event_token ? (
											<> &middot; token {row.event_token}</>
										) : null}
									</span>
								</div>
								<button
									type="button"
									className={BTN_GHOST}
									onClick={() => deleteMut.mutate(row.id)}
									disabled={deleteMut.isPending}
									data-testid={`adjust-postback-template-delete-${row.id}`}
								>
									Delete
								</button>
							</li>
						))}
					</ul>
				)}
			</section>
		</main>
	);
}

export default AdjustPostbackTemplatePage;
